import { stylesheet } from 'astroturf';
import { FunctionalComponent, Ref } from 'preact';
import { useState } from 'preact/hooks';
import { forwardRef } from 'preact/compat';
import { BaseNav, Nav, NavProps, NavState } from '@accedo/spatial-nav';
import { VirtualKeyboardClass } from '@accedo/virtual-keyboard';
import BackButton from '##/assets/images/BackButton.svg';
import SearchIcon from '##/assets/icons/Nav-Icon-Search.svg';
import FocusedBackButton from '##/assets/images/FocusedBackButton.svg';
import { SEARCH_BUTTON_NAV_ID } from '##/utils';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100vw;
    height: 100vh;
    position: absolute;
    top: 0;
    background-color: $pure-black;
    z-index: 1000;
    padding-left: 230px;
    padding-right: 100px;
  }

  .search-bar {
    display: flex;
    position:absolute;
    top: 18vh;
    right: 17.5vw;
    justify-content: flex-start;
    align-items: center;
    font-size: x-large;
    background-color: white;
    height: 50px;
    border-radius: pxToVw(25);
    color: $search-font-grey;
    width:836px;
    z-index:2;
  }

  .search-bar:empty:before {
    content: "\200b";
  }

  .search-bar-icon {
    fill: rgb(137, 148, 164);
    height: pxToVh(13);
    height: 100%;
    margin-left: 20px;

  }

  .back-button {
      position: absolute;
      top: 4vh;
      left: 3vw;

  }

  .key {
    width: 50px;
    height: 50px;
    background-color: $keyboard-gray;
    text-align: center;
    opacity: 0.85;
    display:flex;
    align-items: center;
    justify-content:center;
    color: white;
    margin: 5px;
    font-size: x-large;
    &:global(.current-focus){
      background-color: $pure-white;
      color: black;
    }
  }
  .backspace {
    width: 110px;
    height: 50px;
    background-color: $keyboard-gray;
    opacity: 0.85;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    margin: 5px;
    font-size: medium;
    fill: white;
    &:global(.current-focus){
      background-color: $pure-white;
      fill: black;
    };
  }


  .spaceKey {
    width: 331px;
    height: 50px;
    background-color: $keyboard-gray;
    opacity: 0.85;
    text-align: center;
    line-height: pxToVw(100);
    display:flex;
    align-items: center;
    justify-content:center;
    color: white;
    margin: 5px;
    font-size: x-large;
    fill: white;
    &:global(.current-focus){
      background-color: $pure-white;
      color: black;
    }
  }
  .enterKey {
    width: 187px;
    height: 50px;
    background: transparent linear-gradient(90deg, $ten-play-blue 0%, $ten-enter-blue 100%) 0% 0% no-repeat padding-box;
    opacity: 0.85;
    text-align: center;
    line-height: pxToVw(100);
    display:flex;
    align-items: center;
    justify-content:center;
    align-self: center;
    color: white;
    margin: 5px;
    font-size: x-large;
    &:global(.current-focus){
      background: $pure-white;
      color: black;
    }
}

.specialKeys {
    font-family: 'barlowsemicondensed';
    width: 81px;
    height: 50px;
    background-color: $keyboard-gray;
    opacity: 0.85;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: pxToVw(100);
    color: white;
    margin: 5px;
    font-size: x-large;
    &:global(.current-focus){
      background-color: $pure-white;
      color: black;
    }

  }
  
`;

interface VirtualKeyboardProps {
  onSearchTermSelected: (term: string) => void;
  onExitKeyPressed: () => void;
  defaultFocusId: string;
  initialText: string;
  navRef: Ref<BaseNav<NavProps, NavState>>;
}

const VirtualKeyboardModal: FunctionalComponent<VirtualKeyboardProps> = ({
  onExitKeyPressed,
  onSearchTermSelected,
  navRef,
  initialText,
}) => {
  const [buttonIsFocused, setButtonIsFocused] = useState(false);
  return (
    <Nav.Horizontal id="virtual-search" ref={navRef}>
      <div className={styles.container} id="virtual-keyboard">
        <Nav.Focusable
          id={SEARCH_BUTTON_NAV_ID}
          onSelect={onExitKeyPressed}
          onFocus={() => setButtonIsFocused(true)}
          onBlur={() => setButtonIsFocused(false)}
        >
          <div className={styles.backButton} id={SEARCH_BUTTON_NAV_ID}>
            {buttonIsFocused ? <FocusedBackButton /> : <BackButton />}
          </div>
        </Nav.Focusable>
        <VirtualKeyboardClass
          onEnter={(content: string) => {
            onSearchTermSelected(content);
            onExitKeyPressed();
          }}
          content={initialText}
          styleKeyDict={{
            '#%=': styles.specialKeys,
            ABC: styles.specialKeys,
            backspace: styles.backspace,
            Enter: styles.enterKey,
            space: styles.spaceKey,
          }}
          searchBar={(searchString: string) => (
            <span className={styles.searchBar}>
              <SearchIcon className={styles.searchBarIcon} />
              {searchString}
            </span>
          )}
          defaultKeyStyles={styles.key}
        />
      </div>
      <div className={styles.content} />
    </Nav.Horizontal>
  );
};

export const VirtualKeyboardRef = forwardRef<BaseNav<NavProps, NavState>, VirtualKeyboardProps>(
  (props: VirtualKeyboardProps, ref: Ref<BaseNav<NavProps, NavState>>) => {
    const modalProps: VirtualKeyboardProps = {
      ...props,
      navRef: ref,
    };
    return <VirtualKeyboardModal {...modalProps} />;
  },
);
